<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <!-- z-[1100] so the overlay covers the sticky basket bar (MerchantBasket uses z-500), the
           z-999 MarketingPopup, and the z-1000 sheets - ModalSheet (question + upsell modals) and
           the cart drawer. BOT-1422 opens this modal from inside the upsell modal to pick a
           variant, so ranking below a sheet would open it behind one. -->
      <div
        v-if="isOpen"
        ref="root"
        data-testid="overlay-root"
        class="fixed inset-0 z-[1100] flex justify-center"
        :class="alignmentClasses"
        @mousedown="onPointerDown"
        @click="onRootClick"
      >
        <div data-testid="overlay-backdrop" class="absolute inset-0 bg-gray-brand-900 bg-opacity-60"></div>
        <Transition
          appear
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
        >
          <div
            ref="panel"
            data-testid="overlay-panel"
            role="dialog"
            aria-modal="true"
            :aria-label="ariaLabel"
            tabindex="-1"
            class="relative flex w-full flex-col overflow-hidden bg-white shadow-xl outline-none"
            :class="panelClasses"
            :style="panelStyle"
            @touchstart="onTouchStart"
            @touchmove="onTouchMove"
            @touchend="onTouchEnd"
            @touchcancel="onTouchEnd"
          >
            <!-- The grabber for pull-to-dismiss. Sheet-only, and it sits over the hero rather than
                 taking a row of its own so the photo stays full-bleed. -->
            <span
              v-if="dragEnabled"
              data-testid="overlay-drag-handle"
              aria-hidden="true"
              class="absolute top-2 left-1/2 z-10 h-1 w-10 -translate-x-1/2 rounded-full bg-white/70 shadow-sm md:hidden"
            ></span>
            <slot />
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { useScrollLock } from '@/composables/useScrollLock'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

// Paired with a rule in assets/styles/index.css that drops the outline while this is present.
export const FOCUS_RESTORED_ATTRIBUTE = 'data-focus-restored'

// Pull-to-dismiss. Closing needs either a long pull or a short fast flick, so a lazy drag that
// stops a third of the way down snaps back rather than losing the customer's configuration.
export const DRAG_CLOSE_FRACTION = 0.25
export const DRAG_FLICK_MIN_PX = 60
export const DRAG_FLICK_VELOCITY = 0.5
export const SNAP_BACK_MS = 200
// A dismissed sheet finishes travelling off the bottom before it is torn down, so the panel is
// gone from view by the time the backdrop fades rather than vanishing under the finger.
export const DISMISS_MS = 200
// Tailwind's `md`, the breakpoint at which the panel stops being a sheet.
export const SHEET_MAX_WIDTH_QUERY = '(min-width: 768px)'

export default {
  name: 'Overlay',
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    open: {
      type: Boolean,
      required: false,
      default: null
    },
    ariaLabel: {
      type: String,
      required: false,
      default: null
    },
    closeOnBackdrop: {
      type: Boolean,
      required: false,
      default: true
    },
    closeOnEsc: {
      type: Boolean,
      required: false,
      default: true
    },
    fullScreenOnMobile: {
      type: Boolean,
      required: false,
      default: true
    },
    dragToClose: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  emits: ['close', 'update:modelValue'],
  setup() {
    const { lock, unlock } = useScrollLock()
    return { lock, unlock }
  },
  data() {
    return {
      pointerDownInsidePanel: false,
      previouslyFocused: null,
      dragArmed: false,
      dragging: false,
      dragActive: false,
      dismissing: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartAt: 0,
      dragY: 0,
      snapBackTimer: null,
      dismissTimer: null
    }
  },
  computed: {
    isOpen() {
      return this.open === null ? Boolean(this.modelValue) : Boolean(this.open)
    },
    // `items-stretch` rather than a `100dvh` height: the root is already `fixed inset-0`, so
    // stretching fills exactly the visible viewport without depending on how a mobile browser
    // measures vh against its collapsing chrome.
    alignmentClasses() {
      return this.fullScreenOnMobile ? 'items-stretch md:items-center md:p-4' : 'items-center p-4'
    },
    panelClasses() {
      return this.fullScreenOnMobile
        ? 'max-h-none rounded-none md:h-auto md:max-h-[90vh] md:max-w-xl md:rounded-lg'
        : 'max-h-[90vh] max-w-xl rounded-lg'
    },

    // Only present while a drag is in flight, so the inline transition cannot override the enter
    // animation's `transition` shorthand on the way in.
    panelStyle() {
      if (!this.dragActive) return null
      const duration = this.dismissing ? DISMISS_MS : SNAP_BACK_MS
      return {
        transform: this.dragY ? `translateY(${this.dragY}px)` : null,
        transition: this.dragging ? 'none' : `transform ${duration}ms ease-out`
      }
    },

    dragEnabled() {
      return this.fullScreenOnMobile && this.dragToClose
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(value) {
        if (value) this.activate()
        else this.deactivate()
      }
    }
  },
  beforeUnmount() {
    this.deactivate()
  },
  methods: {
    activate() {
      this.previouslyFocused = typeof document === 'undefined' ? null : document.activeElement
      this.resetDrag()
      this.lock()
      if (typeof document !== 'undefined') document.addEventListener('keydown', this.onKeydown, true)
      this.$nextTick(this.focusPanel)
    },
    deactivate() {
      if (typeof document !== 'undefined') document.removeEventListener('keydown', this.onKeydown, true)
      this.unlock()
      this.pointerDownInsidePanel = false
      this.resetDrag()
      this.restoreFocus()
    },
    requestClose() {
      this.$emit('update:modelValue', false)
      this.$emit('close')
    },

    isSheetViewport() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
      return !window.matchMedia(SHEET_MAX_WIDTH_QUERY).matches
    },

    // A pull that begins inside a list the customer has already scrolled is them scrolling back up,
    // not dismissing. Without this the modifier list becomes unusable the moment it is scrolled.
    startedInsideScrolledRegion(node) {
      const panel = this.$refs.panel
      let element = node
      while (element && element !== panel) {
        if (element.scrollTop > 0) return true
        element = element.parentElement
      }
      return false
    },

    onTouchStart(event) {
      if (!this.dragEnabled || !this.isSheetViewport()) return
      // Already on its way out; a second grab must not drag it back into view.
      if (this.dismissing) return
      const touches = event.touches || []
      // Ignore pinches and other multi-finger gestures.
      if (touches.length !== 1) return
      if (this.startedInsideScrolledRegion(event.target)) return

      this.clearSnapBack()
      this.dragArmed = true
      this.dragActive = true
      this.dragStartX = touches[0].clientX || 0
      this.dragStartY = touches[0].clientY
      this.dragStartAt = Date.now()
      this.dragY = 0
    },

    onTouchMove(event) {
      if (!this.dragArmed) return
      const touch = (event.touches || [])[0]
      if (!touch) return

      const delta = touch.clientY - this.dragStartY
      const deltaX = (touch.clientX || 0) - this.dragStartX
      if (!this.dragging && Math.abs(deltaX) > Math.abs(delta)) {
        this.dragArmed = false
        this.dragActive = false
        return
      }
      // Upward is the body scrolling; leave it alone rather than rubber-banding.
      if (delta <= 0) {
        this.dragY = 0
        return
      }
      this.dragging = true
      this.dragY = delta
      if (event.cancelable) event.preventDefault()
    },

    onTouchEnd() {
      if (!this.dragArmed) return

      const height = (this.$refs.panel && this.$refs.panel.offsetHeight) || 0
      // Floor the divisor at 1ms rather than guarding elapsed === 0 with a velocity of zero: a
      // flick quick enough to land inside a single millisecond is the fastest gesture there is,
      // and treating it as the slowest would reject exactly the flicks that should close.
      const elapsed = Date.now() - this.dragStartAt
      const velocity = this.dragY / Math.max(elapsed, 1)
      const pulledFar = height > 0 && this.dragY > height * DRAG_CLOSE_FRACTION
      const flicked = this.dragY > DRAG_FLICK_MIN_PX && velocity > DRAG_FLICK_VELOCITY

      this.dragArmed = false
      this.dragging = false

      if (pulledFar || flicked) {
        this.dismiss(height)
        return
      }

      // Snap back under the CSS transition, then drop the inline style so it cannot interfere with
      // the next enter animation.
      this.dragY = 0
      this.snapBackTimer = setTimeout(() => {
        this.dragActive = false
        this.snapBackTimer = null
      }, SNAP_BACK_MS)
    },

    // Carry the panel the rest of the way off the bottom, then tear the overlay down. Closing on
    // release instead would leave it frozen wherever the finger stopped while the backdrop faded
    // out around it.
    dismiss(height) {
      this.dismissing = true
      this.dragY = height || this.dragY
      this.dismissTimer = setTimeout(() => {
        this.dismissTimer = null
        this.requestClose()
      }, DISMISS_MS)
    },

    clearSnapBack() {
      if (!this.snapBackTimer) return
      clearTimeout(this.snapBackTimer)
      this.snapBackTimer = null
    },

    clearDismiss() {
      if (!this.dismissTimer) return
      clearTimeout(this.dismissTimer)
      this.dismissTimer = null
    },

    resetDrag() {
      this.clearSnapBack()
      this.clearDismiss()
      this.dragArmed = false
      this.dragging = false
      this.dragActive = false
      this.dismissing = false
      this.dragY = 0
    },
    focusableElements() {
      const panel = this.$refs.panel
      if (!panel) return []
      return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
      )
    },
    focusPanel() {
      const target = this.focusableElements()[0] || this.$refs.panel
      if (target && typeof target.focus === 'function') target.focus()
    },
    restoreFocus() {
      const element = this.previouslyFocused
      this.previouslyFocused = null
      if (!element || typeof element.focus !== 'function') return
      if (typeof document !== 'undefined' && !document.contains(element)) return
      // Nothing was focused when the modal opened, so activeElement was the body. Focusing it is a
      // no-op, and marking it would leave the attribute there for good - the body never blurs.
      if (typeof document !== 'undefined' && element === document.body) return

      // Handing focus back keeps the tab order sane, but the browser treats the restore as
      // keyboard navigation the moment any key was pressed while the modal was open - Escape on
      // its own is enough - and paints its focus ring on the card the user just closed. Marking
      // the element suppresses that one ring; the mark clears on blur so tabbing to the same card
      // later still shows it.
      element.setAttribute(FOCUS_RESTORED_ATTRIBUTE, '')
      element.addEventListener('blur', () => element.removeAttribute(FOCUS_RESTORED_ATTRIBUTE), { once: true })
      element.focus()
    },
    isInsidePanel(node) {
      const panel = this.$refs.panel
      if (!panel || !node) return false
      return panel === node || panel.contains(node)
    },
    onPointerDown(event) {
      this.pointerDownInsidePanel = this.isInsidePanel(event.target)
    },
    onRootClick(event) {
      // A drag that starts in the panel and releases on the backdrop must not close.
      const startedInsidePanel = this.pointerDownInsidePanel
      this.pointerDownInsidePanel = false
      if (!this.closeOnBackdrop) return
      if (startedInsidePanel) return
      if (this.isInsidePanel(event.target)) return
      this.requestClose()
    },
    onKeydown(event) {
      if (!this.isOpen) return
      if (event.key === 'Escape') {
        if (!this.closeOnEsc) return
        event.preventDefault()
        this.requestClose()
        return
      }
      if (event.key !== 'Tab') return

      const elements = this.focusableElements()
      const panel = this.$refs.panel
      if (elements.length === 0) {
        event.preventDefault()
        if (panel) panel.focus()
        return
      }

      const first = elements[0]
      const last = elements[elements.length - 1]
      const active = typeof document === 'undefined' ? null : document.activeElement

      if (!this.isInsidePanel(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}
</script>
